import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  PlusIcon,
  WalletIcon,
  FunnelIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  MagnifyingGlassIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { TOKEN_SYMBOL, formatTokenAmount } from '../config/token';
import { usePagination } from '../hooks/usePagination';
import Pagination from './Pagination';
import RepairFeedbackBanner from './RepairFeedbackBanner';
import { walletService } from '../services/walletService';

interface Wallet {
  id: string;
  publicKey: string;
  type: string;
  balance: number;
  associatedTokenAccount: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Filters {
  type: string;
  isActive: string;
  search: string;
}

const EMPTY_FILTERS: Filters = {
  type: '',
  isActive: '',
  search: '',
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

const WalletManagement: React.FC = () => {
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newWalletAddress, setNewWalletAddress] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [updatingBalance, setUpdatingBalance] = useState<string | null>(null);
  const [repairingWallet, setRepairingWallet] = useState<string | null>(null);
  const [repairFeedback, setRepairFeedback] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const { page, pageSize, setPage, setPageSize } = usePagination();

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(EMPTY_FILTERS);

  const fetchWallets = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('limit', String(pageSize));
      if (appliedFilters.type) params.append('type', appliedFilters.type);
      if (appliedFilters.isActive) params.append('isActive', appliedFilters.isActive);
      if (appliedFilters.search) params.append('search', appliedFilters.search);

      const response = await axios.get(`${API_BASE_URL}/wallet?${params.toString()}`);
      setWallets(response.data.data);
      setTotalItems(response.data.meta.total);
      setTotalPages(response.data.meta.totalPages);
    } catch (err: any) {
      console.error('Error loading wallets:', err);
      if (err.response?.status === 403) {
        setError('Authentication required. Please log in again.');
      } else {
        setError('Failed to load wallets: ' + (err.response?.data?.message || err.message));
      }
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedFilters]);

  useEffect(() => {
    fetchWallets();
  }, [fetchWallets]);

  const applyFilters = () => {
    setAppliedFilters(filters);
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const createWallet = async () => {
    try {
      setCreating(true);
      const response = await axios.get(`${API_BASE_URL}/blockchain/create`);
      if (response.data && response.data.accountPublic) {
        setNewWalletAddress(response.data.accountPublic);
        fetchWallets();
      } else {
        alert('Wallet created but response format unexpected');
      }
    } catch (err: any) {
      console.error('Error creating wallet:', err);
      alert('Failed to create wallet: ' + (err.response?.data?.message || err.message));
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Wallet address copied to clipboard!');
  };

  const handleUpdateBalance = async (walletId: string) => {
    try {
      setUpdatingBalance(walletId);
      await axios.post(`${API_BASE_URL}/wallet/${walletId}/update-balance`);
      await fetchWallets();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update balance');
    } finally {
      setUpdatingBalance(null);
    }
  };

  const handleRepairWallet = async (walletId: string) => {
    try {
      setRepairingWallet(walletId);
      setError('');
      setRepairFeedback(null);
      const result = await walletService.repairWallet(walletId);
      await fetchWallets();
      setRepairFeedback(result.message);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to repair wallet');
    } finally {
      setRepairingWallet(null);
    }
  };

  const updateAllBalances = async () => {
    try {
      setUpdatingBalance('all');
      await axios.post(`${API_BASE_URL}/wallet/update-all-balances`);
      await fetchWallets();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update all balances');
    } finally {
      setUpdatingBalance(null);
    }
  };

  // Server returns the filtered page; we only sort the current page for display.
  const displayedWallets = [...wallets].sort((a, b) => b.balance - a.balance);

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'hot':
        return 'bg-red-500/15 text-red-300 ring-1 ring-inset ring-red-500/30';
      case 'normal':
        return 'bg-blue-500/15 text-blue-300 ring-1 ring-inset ring-blue-500/30';
      default:
        return 'bg-slate-700 text-slate-300 ring-1 ring-inset ring-slate-600';
    }
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleString();

  const inputClass =
    'mt-1 block w-full rounded-md border-slate-600 bg-slate-700 text-slate-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm';
  const labelClass = 'block text-sm font-medium text-slate-300';

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-100">Wallet Management</h1>
          <p className="mt-2 text-sm text-slate-400">
            Manage hot wallet, merchant wallets, and customer wallets
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:flex sm:space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>
          <button
            onClick={() => fetchWallets()}
            className="inline-flex items-center px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={updateAllBalances}
            disabled={updatingBalance === 'all'}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-green-500 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${updatingBalance === 'all' ? 'animate-spin' : ''}`} />
            {updatingBalance === 'all' ? 'Updating All...' : 'Update All Balances'}
          </button>
          <button
            onClick={createWallet}
            disabled={creating}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-900 sm:w-auto disabled:opacity-50"
          >
            {creating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating...
              </>
            ) : (
              <>
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Wallet
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4">
          <div className="text-sm text-red-300">{error}</div>
        </div>
      )}

      {repairFeedback && (
        <RepairFeedbackBanner message={repairFeedback} onDismiss={() => setRepairFeedback(null)} />
      )}

      {/* Filters */}
      {showFilters && (
        <div className="rounded-xl border border-slate-700 bg-slate-800 p-6 shadow-sm">
          <h3 className="text-lg font-medium text-slate-100 mb-4">Filter Wallets</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Type</label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                className={inputClass}
              >
                <option value="">All Types</option>
                <option value="hot">Hot</option>
                <option value="normal">Normal</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <select
                value={filters.isActive}
                onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
                className={inputClass}
              >
                <option value="">All Statuses</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Search</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyFilters();
                }}
                placeholder="Wallet address..."
                className={inputClass}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-3">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm font-medium text-slate-200 bg-slate-700 hover:bg-slate-600 rounded-md"
            >
              Clear Filters
            </button>
            <button
              onClick={applyFilters}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500"
            >
              <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Wallet Created Modal */}
      {newWalletAddress && (
        <div className="fixed inset-0 bg-black/60 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-slate-700 w-96 shadow-xl rounded-xl bg-slate-800">
            <div className="mt-3">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-green-500/15 rounded-full">
                <CheckCircleIcon className="h-6 w-6 text-green-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-100 text-center mt-4 mb-4">Wallet Created Successfully!</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-300 mb-2">Wallet Address:</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={newWalletAddress}
                    readOnly
                    className="flex-1 px-3 py-2 border border-slate-600 rounded-md bg-slate-900 text-slate-200 text-sm font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(newWalletAddress)}
                    className="px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
                    title="Copy to clipboard"
                  >
                    <ClipboardDocumentIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="flex justify-center">
                <button
                  onClick={() => setNewWalletAddress(null)}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Wallets List */}
      <div className="rounded-xl border border-slate-700 bg-slate-800 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
          </div>
        ) : (
          <ul className="divide-y divide-slate-700">
            {displayedWallets.map((wallet) => (
              <li key={wallet.id} className="hover:bg-slate-700/40 transition-colors">
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-indigo-500/15 flex items-center justify-center">
                          <WalletIcon className="h-6 w-6 text-indigo-400" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-slate-100 font-mono">
                            {wallet.publicKey.substring(0, 8)}...{wallet.publicKey.slice(-8)}
                          </p>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(wallet.type)}`}>
                            {wallet.type}
                          </span>
                          {wallet.isActive ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/15 text-green-300 ring-1 ring-inset ring-green-500/30">
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-700 text-slate-300 ring-1 ring-inset ring-slate-600">
                              Inactive
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex items-center space-x-4 text-sm text-slate-400">
                          <span>Balance: {formatTokenAmount(wallet.balance)} {TOKEN_SYMBOL}</span>
                          <span>Created: {formatDate(wallet.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleRepairWallet(wallet.id)}
                        disabled={repairingWallet === wallet.id}
                        className="inline-flex items-center px-3 py-1 border border-slate-600 text-sm leading-4 font-medium rounded-md text-slate-200 bg-slate-700 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500 disabled:opacity-50"
                        title="Fund SOL and create USDC token account if missing"
                      >
                        <WrenchScrewdriverIcon className={`h-4 w-4 mr-1 ${repairingWallet === wallet.id ? 'animate-spin' : ''}`} />
                        {repairingWallet === wallet.id ? 'Repairing...' : 'Repair'}
                      </button>
                      <button
                        onClick={() => handleUpdateBalance(wallet.id)}
                        disabled={updatingBalance === wallet.id}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-green-500 disabled:opacity-50"
                      >
                        <ArrowPathIcon className={`h-4 w-4 mr-1 ${updatingBalance === wallet.id ? 'animate-spin' : ''}`} />
                        {updatingBalance === wallet.id ? 'Updating...' : 'Update Balance'}
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
        {!loading && displayedWallets.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-400">No wallets found</p>
          </div>
        )}
      </div>

      {!loading && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}
    </div>
  );
};

export default WalletManagement;
